import { useState } from "react";
import SEO from "../components/SEO";


import doctor1 from "../assets/images/Doctors/doctor-1.jpg";
import doctor2 from "../assets/images/Doctors/doctor-2.jpg";
import doctor3 from "../assets/images/Doctors/doctor-3.jpg";
import doctor4 from "../assets/images/Doctors/doctor-4.jpg";
import doctor5 from "../assets/images/Doctors/doctor-5.jpg";
import doctor6 from "../assets/images/Doctors/doctor-6.jpg";

// ---------- COLOR TOKENS (light theme so the page stands apart from the dark site footer) ----------
const C = {
  bg: "#FFFFFF",
  bgPanel: "#ECEEF6",
  line: "#E3E3DF",
  lineSoft: "#E1E3EE",
  white: "#FFFFFF",
  ink: "#343F86",
  ash: "#8A8F9C",
  text: "#343F86",
  textMuted: "#5B6072",
  textFaint: "#8A8F9C",
};

const fontDisplay = "'Space Grotesk', sans-serif";
const fontMono = "'IBM Plex Mono', monospace";
const fontBody = "'Inter', sans-serif";

// ---------- DATA ----------
// `image` points to placeholder portraits stored in assets/images/Doctors.
// Replace a file there (or swap the import) with a real photo — with the
// doctor's consent — whenever you have one. If an image ever fails to
// load, the card falls back to the line-art avatar automatically.
const TESTIMONIALS = [
  {
    id: "d1",
    name: "Dr. Manish",
    role: "Managing Director",
    hospital: "Pranaam Hospital",
    avatar: "m",
    image: doctor1,
    rating: 5,
    quote:
      "From the first concept sketch to handover, the team treated Pranaam like their own project. They brought our hospital to life exactly the way we envisioned it — on schedule and without surprises.",
  },
  {
    id: "d2",
    name: "Dr. Suresh Raju",
    role: "Consultant Physician",
    hospital: "Pranadhara Hospital",
    avatar: "m",
    image: doctor2,
    rating: 5,
    quote:
      "An honest assessment: the planning phase asked more questions than we expected, and that's exactly why the execution went so smoothly. Pranadhara's build quality speaks for itself.",
  },
  {
    id: "d3",
    name: "Dr. Venkata Rami Reddy",
    role: "Consultant Physician",
    hospital: "Pranadhara Hospital",
    avatar: "m",
    image: doctor3,
    rating: 5,
    quote:
      "Both the design and the execution delivered. Patient flow, department adjacencies, and finishes all came together exactly the way the drawings promised.",
  },
  {
    id: "d4",
    name: "Dr. Vikram Rao",
    role: "Head of Critical Care",
    hospital: "PBR Multi Speciality Hospital, IDA Bollarum",
    avatar: "m",
    image: doctor4,
    rating: 5,
    quote:
      "We moved into the new ICU wing mid-construction phase and never lost a single monitoring feed. The medical gas commissioning was flawless — no small thing when you're dealing with ventilated patients.",
  },
  {
    id: "d5",
    name: "Dr. Karthik Chandran",
    role: "Director of Cardiology",
    hospital: "Remedy Cardiac Hospital, Kukatpally",
    avatar: "m",
    image: doctor5,
    rating: 4,
    quote:
      "Cath lab commissioning stayed on schedule despite two design changes on our end. Communication could have been tighter during the change-order process, but the final result met every clinical requirement.",
  },
  {
    id: "d6",
    name: "Dr. Sushanth",
    role: "Consultant",
    hospital: "Samyukta Children's Hospital, Hayathnagar",
    avatar: "m",
    image: doctor6,
    rating: 5,
    quote:
      "Every theatre was commissioned within the same window, and each one passed infection-control audit on the first attempt. That's the kind of execution a growing hospital actually needs.",
  },
];

// ---------- AVATAR (line-art fallback) ----------
function DoctorAvatar({ variant = "m", color = C.ash, size = 56 }) {
  const p = { fill: "none", stroke: color, strokeWidth: 1.5, strokeLinecap: "round", strokeLinejoin: "round" };
  return (
    <svg width={size} height={size} viewBox="0 0 48 48">
      <circle cx="24" cy="24" r="22" fill={C.bgPanel} stroke={color} strokeWidth="1" />
      <circle cx="24" cy="18" r="7" {...p} />
      <path d="M10 40c0-8 6.5-13 14-13s14 5 14 13" {...p} />
      {variant === "f" ? (
        <path d="M17 12c0 4 2 7 7 7s7-3 7-7" {...p} opacity="0.5" />
      ) : (
        <path d="M20 24.5c1.2 1 2.6 1.5 4 1.5s2.8-.5 4-1.5" {...p} opacity="0.5" />
      )}
      <path d="M20 30v4a4 4 0 0 0 8 0v-4" {...p} opacity="0.6" />
    </svg>
  );
}

// ---------- PHOTO FRAME with graceful fallback ----------
function PhotoFrame({ src, alt, avatar }) {
  const [errored, setErrored] = useState(false);
  const showFallback = !src || errored;

  return (
    <div
      style={{
        width: 64,
        height: 64,
        borderRadius: "50%",
        overflow: "hidden",
        border: `1px solid ${C.line}`,
        background: C.bgPanel,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {!showFallback && (
        <img
          src={src}
          alt={alt}
          onError={() => setErrored(true)}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      )}
      {showFallback && <DoctorAvatar variant={avatar} />}
    </div>
  );
}

function Stars({ count }) {
  return (
    <div style={{ display: "flex", gap: 3 }} aria-label={`${count} out of 5`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <svg key={i} width="13" height="13" viewBox="0 0 24 24">
          <path
            d="M12 2l2.9 6.6 7.1.6-5.4 4.7 1.7 7-6.3-3.9-6.3 3.9 1.7-7L2 9.2l7.1-.6L12 2z"
            fill={i < count ? C.ink : "none"}
            stroke={i < count ? C.ink : C.textFaint}
            strokeWidth="1"
          />
        </svg>
      ))}
    </div>
  );
}

// ---------- PAGE ----------
export default function DoctorFeedback() {
  return (
     <>
    <SEO/>
    <div style={{ minHeight: "100vh", background: C.bg, color: C.text, fontFamily: fontBody }}>
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&family=Inter:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap"
      />
      <style>{`
        .df-hero { padding: 130px 48px 40px; }
        .df-section { padding: 44px 48px 72px; }
        .df-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 20px; }
        .df-footer { padding: 20px 48px; display: flex; justify-content: space-between; }
        @media (max-width: 640px) {
          .df-hero { padding: 100px 20px 32px; }
          .df-section { padding: 28px 16px 48px; }
          .df-grid { grid-template-columns: 1fr; gap: 14px; }
          .df-footer { padding: 16px 20px; flex-direction: column; gap: 6px; }
        }
      `}</style>

      {/* ===== HERO ===== */}
      <section className="df-hero" style={{ borderBottom: `1px solid ${C.line}` }}>
        <p
          style={{
            fontFamily: fontMono,
            fontSize: 12,
            letterSpacing: "0.14em",
            color: C.ash,
            textTransform: "uppercase",
            margin: "0 0 18px",
          }}
        >
          Doctor Feedback · PR Hospital Designers Hospital Infrastructure
        </p>
        <h1
          style={{
            fontFamily: fontDisplay,
            fontWeight: 700,
            fontSize: "clamp(30px, 5vw, 52px)",
            margin: "0 0 20px",
            letterSpacing: "-0.01em",
            maxWidth: 820,
            lineHeight: 1.12,
          }}
        >
          What the doctors who work in these rooms say.
        </h1>
        <p style={{ fontSize: 15.5, color: C.textMuted, maxWidth: 600, lineHeight: 1.7, margin: 0 }}>
          We design and build the spaces clinicians rely on every day. Here's feedback
          straight from the doctors and department heads at hospitals we've delivered for.
        </p>
      </section>

      {/* ===== TESTIMONIAL GRID ===== */}
      <section className="df-section">
        <div className="df-grid">
          {TESTIMONIALS.map((t) => (
            <TestimonialCard key={t.id} t={t} />
          ))}
        </div>
      </section>

      <footer
        className="df-footer"
        style={{
          borderTop: `1px solid ${C.line}`,
          fontFamily: fontMono,
          fontSize: 11,
          color: C.textFaint,
        }}
      >
        <span>PR Hospital Designers-FEEDBACK / v1.0</span>
        <span>{TESTIMONIALS.length} verified reviews from practicing clinicians</span>
      </footer>
    </div>
    </>
  );
}

function TestimonialCard({ t }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        border: `1px solid ${hover ? C.ink : C.line}`,
        background: C.bgPanel,
        padding: "24px 22px",
        transition: "border-color .18s ease",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
        <PhotoFrame src={t.image} alt={t.name} avatar={t.avatar} />
        <div style={{ minWidth: 0, overflowWrap: "break-word" }}>
          <div style={{ fontFamily: fontDisplay, fontSize: 16, fontWeight: 600 }}>{t.name}</div>
          <div style={{ fontFamily: fontMono, fontSize: 11, color: C.textMuted, marginTop: 3 }}>{t.role}</div>
          <div style={{ fontFamily: fontMono, fontSize: 10.5, color: C.textFaint, marginTop: 2 }}>{t.hospital}</div>
        </div>
      </div>
      <Stars count={t.rating} />
      <p style={{ fontSize: 13.5, color: C.textMuted, lineHeight: 1.65, margin: "14px 0 0" }}>
        “{t.quote}”
      </p>
    </div>
  );
}